import { create, fromJson, JsonValue, toJson } from '@bufbuild/protobuf';
import { apiClient } from './request';
import { Memory, MemoryClass } from '@/proto/agentplatform/v1/memory_pb';
import {
  Skill,
  SkillGroup,
  SkillGroupClass,
  SkillVersion,
  SkillFile,
  SkillFileContent,
} from '@/proto/agentplatform/v1/skill_pb';
import {
  ListMemoryClassesResponseSchema,
  CreateMemoryClassRequestSchema,
  CreateMemoryClassResponseSchema,
  GetMemoryClassRequestSchema,
  GetMemoryClassResponseSchema,
  UpdateMemoryClassRequestSchema,
  UpdateMemoryClassResponseSchema,
  DeleteMemoryClassRequestSchema,
  ListMemoriesResponseSchema,
  CreateMemoryRequestSchema,
  CreateMemoryResponseSchema,
  GetMemoryRequestSchema,
  GetMemoryResponseSchema,
  UpdateMemoryRequestSchema,
  UpdateMemoryResponseSchema,
  DeleteMemoryRequestSchema,
  ListCortexItemsRequestSchema,
  ListCortexItemsResponseSchema,
  GetCortexItemRequestSchema,
  GetCortexItemResponseSchema,
  CreateCortexItemRequestSchema,
  CreateCortexItemResponseSchema,
  UpdateCortexItemRequestSchema,
  UpdateCortexItemResponseSchema,
  DeleteCortexItemRequestSchema,
  DeleteAllCortexItemsRequestSchema,
  SearchCortexItemsRequestSchema,
  SearchCortexItemsResponseSchema,
  CortexMessageSchema,
  type CortexMemoryItem as PbCortexMemoryItem,
} from '@/proto/agentplatform/httpv2/memory_pb';
import {
  ListSkillGroupClassesResponseSchema,
  CreateSkillGroupClassRequestSchema,
  CreateSkillGroupClassResponseSchema,
  GetSkillGroupClassRequestSchema,
  GetSkillGroupClassResponseSchema,
  UpdateSkillGroupClassRequestSchema,
  UpdateSkillGroupClassResponseSchema,
  DeleteSkillGroupClassRequestSchema,
  ListSkillGroupsResponseSchema,
  CreateSkillGroupRequestSchema,
  CreateSkillGroupResponseSchema,
  GetSkillGroupRequestSchema,
  GetSkillGroupResponseSchema,
  UpdateSkillGroupRequestSchema,
  UpdateSkillGroupResponseSchema,
  DeleteSkillGroupRequestSchema,
  ListSkillsResponseSchema,
  GetSkillRequestSchema,
  GetSkillResponseSchema,
  CreateSkillRequestSchema,
  CreateSkillResponseSchema,
  ForkSkillRequestSchema,
  ForkSkillResponseSchema,
  UpdateSkillRequestSchema,
  UpdateSkillResponseSchema,
  DeleteSkillRequestSchema,
  ListSkillVersionsRequestSchema,
  ListSkillVersionsResponseSchema,
  ListSkillVersionsDataSchema,
  GetSkillVersionRequestSchema,
  GetSkillVersionResponseSchema,
  CreateSkillVersionRequestSchema,
  CreateSkillVersionResponseSchema,
  DeleteSkillVersionRequestSchema,
  UpdateSkillVersionContentRequestSchema,
  UpdateSkillVersionContentResponseSchema,
  CloneSkillVersionRequestSchema,
  CloneSkillVersionResponseSchema,
  PromoteSkillVersionRequestSchema,
  PromoteSkillVersionResponseSchema,
  ListSkillVersionFilesRequestSchema,
  ListSkillVersionFilesResponseSchema,
  ListSkillVersionFilesDataSchema,
  GetSkillVersionFileRequestSchema,
  GetSkillVersionFileResponseSchema,
  PutSkillVersionFileRequestSchema,
  PutSkillVersionFileResponseSchema,
  DeleteSkillVersionFileRequestSchema,
  ImportSkillZipRequestSchema,
  ImportSkillZipResponseSchema,
  ExportSkillVersionRequestSchema,
  ExportSkillVersionResponseSchema,
  ExportSkillVersionDataSchema,
} from '@/proto/agentplatform/httpv2/skill_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { CORTEX_MEMORY_SHARED_USER_ID } from '@/constants/memory';

async function sendV2(path: string, body: JsonValue): Promise<JsonValue> {
  const resp = await apiClient.post<JsonValue>(path, body);
  return resp.data;
}

export const cortexApi = {
  listSkillGroupClasses: async (params: ListRequest) => {
    const raw = await sendV2('/v2/ListSkillGroupClasses', toJson(ListRequestSchema, params));
    const resp = fromJson(ListSkillGroupClassesResponseSchema, raw);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  createSkillGroupClass: async (payload: SkillGroupClass): Promise<SkillGroupClass> => {
    const req = create(CreateSkillGroupClassRequestSchema, { skillgroupClass: payload });
    const raw = await sendV2(
      '/v2/CreateSkillGroupClass',
      toJson(CreateSkillGroupClassRequestSchema, req)
    );
    return fromJson(CreateSkillGroupClassResponseSchema, raw).data!;
  },

  getSkillGroupClass: async (id: string): Promise<SkillGroupClass> => {
    const req = create(GetSkillGroupClassRequestSchema, { id });
    const raw = await sendV2(
      '/v2/GetSkillGroupClass',
      toJson(GetSkillGroupClassRequestSchema, req)
    );
    return fromJson(GetSkillGroupClassResponseSchema, raw).data!;
  },

  updateSkillGroupClass: async (id: string, payload: SkillGroupClass): Promise<SkillGroupClass> => {
    const req = create(UpdateSkillGroupClassRequestSchema, { id, skillgroupClass: payload });
    const raw = await sendV2(
      '/v2/UpdateSkillGroupClass',
      toJson(UpdateSkillGroupClassRequestSchema, req)
    );
    return fromJson(UpdateSkillGroupClassResponseSchema, raw).data!;
  },

  deleteSkillGroupClass: async (id: string): Promise<void> => {
    const req = create(DeleteSkillGroupClassRequestSchema, { id });
    await sendV2('/v2/DeleteSkillGroupClass', toJson(DeleteSkillGroupClassRequestSchema, req));
  },

  listSkillGroups: async (params: ListRequest) => {
    const raw = await sendV2('/v2/ListSkillGroups', toJson(ListRequestSchema, params));
    const resp = fromJson(ListSkillGroupsResponseSchema, raw);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  createSkillGroup: async (payload: SkillGroup): Promise<SkillGroup> => {
    const req = create(CreateSkillGroupRequestSchema, { skillgroup: payload });
    const raw = await sendV2('/v2/CreateSkillGroup', toJson(CreateSkillGroupRequestSchema, req));
    return fromJson(CreateSkillGroupResponseSchema, raw).data!;
  },

  getSkillGroup: async (id: string): Promise<SkillGroup> => {
    const req = create(GetSkillGroupRequestSchema, { id });
    const raw = await sendV2('/v2/GetSkillGroup', toJson(GetSkillGroupRequestSchema, req));
    return fromJson(GetSkillGroupResponseSchema, raw).data!;
  },

  updateSkillGroup: async (id: string, payload: SkillGroup): Promise<SkillGroup> => {
    const req = create(UpdateSkillGroupRequestSchema, { id, skillgroup: payload });
    const raw = await sendV2('/v2/UpdateSkillGroup', toJson(UpdateSkillGroupRequestSchema, req));
    return fromJson(UpdateSkillGroupResponseSchema, raw).data!;
  },

  deleteSkillGroup: async (id: string): Promise<void> => {
    const req = create(DeleteSkillGroupRequestSchema, { id });
    await sendV2('/v2/DeleteSkillGroup', toJson(DeleteSkillGroupRequestSchema, req));
  },

  listSkills: async (params: {
    page?: number;
    pageSize?: number;
    keyword?: string;
    userId?: string;
    withVersions?: boolean;
    public?: boolean;
  }) => {
    // ListSkills RPC 入参已统一为 httpv2.ListRequest：
    //   - fuzzy.name：关键字搜索（代理到 cortex 的 keyword）；
    //   - filter.user_id：用户过滤（仅非 public 时生效）；
    //   - filter.with_versions = "true"：携带版本列表；
    //   - filter.public = "true"：列出公共技能。
    const filter: Record<string, string> = {};
    if (params.userId) filter.user_id = params.userId;
    if (params.withVersions) filter.with_versions = 'true';
    if (params.public) filter.public = 'true';
    const keyword = params.keyword?.trim() ?? '';
    const req = create(ListRequestSchema, {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
      fuzzy: keyword ? { name: keyword } : {},
      filter,
    });
    const raw = await sendV2('/v2/ListSkills', toJson(ListRequestSchema, req));
    const resp = fromJson(ListSkillsResponseSchema, raw);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  getSkill: async (skillId: string, withVersions = true): Promise<Skill> => {
    const req = create(GetSkillRequestSchema, { skillId, withVersions });
    const raw = await sendV2('/v2/GetSkill', toJson(GetSkillRequestSchema, req));
    return fromJson(GetSkillResponseSchema, raw).data!;
  },

  createSkill: async (
    payload: Parameters<typeof create<typeof CreateSkillRequestSchema>>[1]
  ): Promise<Skill> => {
    const req = create(CreateSkillRequestSchema, payload);
    const raw = await sendV2('/v2/CreateSkill', toJson(CreateSkillRequestSchema, req));
    return fromJson(CreateSkillResponseSchema, raw).data!;
  },

  forkSkill: async (payload: { skillId: string; skillVersionCode: number }): Promise<Skill> => {
    const req = create(ForkSkillRequestSchema, {
      skillId: payload.skillId,
      skillVersionCode: payload.skillVersionCode,
    });
    const raw = await sendV2('/v2/ForkSkill', toJson(ForkSkillRequestSchema, req));
    return fromJson(ForkSkillResponseSchema, raw).data!;
  },

  updateSkill: async (
    skillId: string,
    payload: Parameters<typeof create<typeof UpdateSkillRequestSchema>>[1]
  ): Promise<Skill> => {
    const req = create(UpdateSkillRequestSchema, { skillId, ...payload });
    const raw = await sendV2('/v2/UpdateSkill', toJson(UpdateSkillRequestSchema, req));
    return fromJson(UpdateSkillResponseSchema, raw).data!;
  },

  deleteSkill: async (skillId: string): Promise<void> => {
    const req = create(DeleteSkillRequestSchema, { skillId });
    await sendV2('/v2/DeleteSkill', toJson(DeleteSkillRequestSchema, req));
  },

  listSkillVersions: async (skillId: string) => {
    const req = create(ListSkillVersionsRequestSchema, { skillId });
    const raw = await sendV2('/v2/ListSkillVersions', toJson(ListSkillVersionsRequestSchema, req));
    return (
      fromJson(ListSkillVersionsResponseSchema, raw).data ?? create(ListSkillVersionsDataSchema, {})
    );
  },

  getSkillVersion: async (
    skillId: string,
    versionCode: number,
    include = 'content'
  ): Promise<SkillVersion> => {
    const req = create(GetSkillVersionRequestSchema, { skillId, versionCode, include });
    const raw = await sendV2('/v2/GetSkillVersion', toJson(GetSkillVersionRequestSchema, req));
    return fromJson(GetSkillVersionResponseSchema, raw).data!;
  },

  createSkillVersion: async (
    skillId: string,
    payload: {
      description?: string;
      storagePath?: string;
      branch?: string;
      isStable?: boolean;
      createdBy?: string;
      content?: string;
    }
  ): Promise<SkillVersion> => {
    const req = create(CreateSkillVersionRequestSchema, { skillId, request: payload });
    const raw = await sendV2(
      '/v2/CreateSkillVersion',
      toJson(CreateSkillVersionRequestSchema, req)
    );
    return fromJson(CreateSkillVersionResponseSchema, raw).data!;
  },

  deleteSkillVersion: async (skillId: string, versionCode: number): Promise<void> => {
    const req = create(DeleteSkillVersionRequestSchema, { skillId, versionCode });
    await sendV2('/v2/DeleteSkillVersion', toJson(DeleteSkillVersionRequestSchema, req));
  },

  updateSkillVersionContent: async (
    skillId: string,
    versionCode: number,
    content: string
  ): Promise<SkillVersion> => {
    const req = create(UpdateSkillVersionContentRequestSchema, {
      skillId,
      versionCode,
      request: { content },
    });
    const raw = await sendV2(
      '/v2/UpdateSkillVersionContent',
      toJson(UpdateSkillVersionContentRequestSchema, req)
    );
    return fromJson(UpdateSkillVersionContentResponseSchema, raw).data!;
  },

  cloneSkillVersion: async (skillId: string, versionCode: number): Promise<SkillVersion> => {
    const req = create(CloneSkillVersionRequestSchema, { skillId, versionCode });
    const raw = await sendV2('/v2/CloneSkillVersion', toJson(CloneSkillVersionRequestSchema, req));
    return fromJson(CloneSkillVersionResponseSchema, raw).data!;
  },

  promoteSkillVersion: async (
    skillId: string,
    versionCode: number,
    payload: { isStable?: boolean; isLatest?: boolean }
  ): Promise<SkillVersion> => {
    const req = create(PromoteSkillVersionRequestSchema, {
      skillId,
      versionCode,
      request: { isStable: !!payload.isStable, isLatest: !!payload.isLatest },
    });
    const raw = await sendV2(
      '/v2/PromoteSkillVersion',
      toJson(PromoteSkillVersionRequestSchema, req)
    );
    return fromJson(PromoteSkillVersionResponseSchema, raw).data!;
  },

  listSkillVersionFiles: async (skillId: string, versionCode: number) => {
    const req = create(ListSkillVersionFilesRequestSchema, { skillId, versionCode });
    const raw = await sendV2(
      '/v2/ListSkillVersionFiles',
      toJson(ListSkillVersionFilesRequestSchema, req)
    );
    return (
      fromJson(ListSkillVersionFilesResponseSchema, raw).data ??
      create(ListSkillVersionFilesDataSchema, {})
    );
  },

  getSkillVersionFile: async (
    skillId: string,
    versionCode: number,
    filePath: string
  ): Promise<SkillFileContent> => {
    const req = create(GetSkillVersionFileRequestSchema, { skillId, versionCode, filePath });
    const raw = await sendV2(
      '/v2/GetSkillVersionFile',
      toJson(GetSkillVersionFileRequestSchema, req)
    );
    return fromJson(GetSkillVersionFileResponseSchema, raw).data!;
  },

  putSkillVersionFile: async (
    skillId: string,
    versionCode: number,
    filePath: string,
    payload: { content: string; encoding?: string }
  ): Promise<SkillFile> => {
    const req = create(PutSkillVersionFileRequestSchema, {
      skillId,
      versionCode,
      filePath,
      request: payload,
    });
    const raw = await sendV2(
      '/v2/PutSkillVersionFile',
      toJson(PutSkillVersionFileRequestSchema, req)
    );
    return fromJson(PutSkillVersionFileResponseSchema, raw).data!;
  },

  deleteSkillVersionFile: async (
    skillId: string,
    versionCode: number,
    filePath: string
  ): Promise<void> => {
    const req = create(DeleteSkillVersionFileRequestSchema, { skillId, versionCode, filePath });
    await sendV2('/v2/DeleteSkillVersionFile', toJson(DeleteSkillVersionFileRequestSchema, req));
  },

  importSkillZip: async (file: File, skillId?: string) => {
    const buffer = new Uint8Array(await file.arrayBuffer());
    const req = create(ImportSkillZipRequestSchema, {
      fileName: file.name,
      file: buffer,
      skillId: skillId ?? '',
    });
    const raw = await sendV2('/v2/ImportSkillZip', toJson(ImportSkillZipRequestSchema, req));
    return fromJson(ImportSkillZipResponseSchema, raw);
  },

  exportSkillVersion: async (skillId: string, versionCode: number) => {
    const req = create(ExportSkillVersionRequestSchema, { skillId, versionCode });
    const raw = await sendV2(
      '/v2/ExportSkillVersion',
      toJson(ExportSkillVersionRequestSchema, req)
    );
    return (
      fromJson(ExportSkillVersionResponseSchema, raw).data ??
      create(ExportSkillVersionDataSchema, {})
    );
  },
};

// ==================== Memory/MemoryClass API ====================

export const memoryApi = {
  listMemories: async (params: ListRequest) => {
    const raw = await sendV2('/v2/ListMemories', toJson(ListRequestSchema, params));
    const resp = fromJson(ListMemoriesResponseSchema, raw);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  createMemory: async (payload: Memory): Promise<Memory> => {
    const req = create(CreateMemoryRequestSchema, { memory: payload });
    const raw = await sendV2('/v2/CreateMemory', toJson(CreateMemoryRequestSchema, req));
    return fromJson(CreateMemoryResponseSchema, raw).data!;
  },

  getMemory: async (id: string): Promise<Memory> => {
    const req = create(GetMemoryRequestSchema, { id });
    const raw = await sendV2('/v2/GetMemory', toJson(GetMemoryRequestSchema, req));
    return fromJson(GetMemoryResponseSchema, raw).data!;
  },

  updateMemory: async (id: string, payload: Memory): Promise<Memory> => {
    const req = create(UpdateMemoryRequestSchema, { id, memory: payload });
    const raw = await sendV2('/v2/UpdateMemory', toJson(UpdateMemoryRequestSchema, req));
    return fromJson(UpdateMemoryResponseSchema, raw).data!;
  },

  deleteMemory: async (id: string): Promise<void> => {
    const req = create(DeleteMemoryRequestSchema, { id });
    await sendV2('/v2/DeleteMemory', toJson(DeleteMemoryRequestSchema, req));
  },
};

export const memoryClassApi = {
  listMemoryClasses: async (params: ListRequest) => {
    const raw = await sendV2('/v2/ListMemoryClasses', toJson(ListRequestSchema, params));
    const resp = fromJson(ListMemoryClassesResponseSchema, raw);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  createMemoryClass: async (payload: MemoryClass): Promise<MemoryClass> => {
    const req = create(CreateMemoryClassRequestSchema, { memoryClass: payload });
    const raw = await sendV2('/v2/CreateMemoryClass', toJson(CreateMemoryClassRequestSchema, req));
    return fromJson(CreateMemoryClassResponseSchema, raw).data!;
  },

  getMemoryClass: async (id: string): Promise<MemoryClass> => {
    const req = create(GetMemoryClassRequestSchema, { id });
    const raw = await sendV2('/v2/GetMemoryClass', toJson(GetMemoryClassRequestSchema, req));
    return fromJson(GetMemoryClassResponseSchema, raw).data!;
  },

  updateMemoryClass: async (id: string, payload: MemoryClass): Promise<MemoryClass> => {
    const req = create(UpdateMemoryClassRequestSchema, { id, memoryClass: payload });
    const raw = await sendV2('/v2/UpdateMemoryClass', toJson(UpdateMemoryClassRequestSchema, req));
    return fromJson(UpdateMemoryClassResponseSchema, raw).data!;
  },

  deleteMemoryClass: async (id: string): Promise<void> => {
    const req = create(DeleteMemoryClassRequestSchema, { id });
    await sendV2('/v2/DeleteMemoryClass', toJson(DeleteMemoryClassRequestSchema, req));
  },
};

// ==================== Cortex Memory Item Types ====================

export interface CortexMemoryItem {
  id: string;
  memory: string;
  hash?: string | null;
  metadata?: Record<string, unknown> | null;
  user_id?: string | null;
  score?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CortexMemoryListResponse {
  items: CortexMemoryItem[];
  total: number;
}

export interface CreateCortexMemoryRequest {
  messages?: { role: string; content: string }[];
  memory?: string;
  store_id: string;
  metadata?: Record<string, unknown>;
  prompt?: string;
  infer?: boolean;
}

export interface SearchCortexMemoryRequest {
  query: string;
  store_id: string;
  filters?: Record<string, unknown>;
  limit?: number;
}

function toCortexMemoryItem(item: PbCortexMemoryItem): CortexMemoryItem {
  return {
    id: item.id,
    memory: item.memory,
    hash: item.hash || null,
    metadata: item.metadata ? (item.metadata as Record<string, unknown>) : null,
    user_id: item.userId || null,
    score: item.score || null,
    created_at: item.createdAt || null,
    updated_at: item.updatedAt || null,
  };
}

// ==================== Cortex Memory Item API ====================

export const cortexMemoryApi = {
  listItems: async (storeId: string, limit?: number): Promise<CortexMemoryListResponse> => {
    const req = create(ListCortexItemsRequestSchema, {
      storeId,
      userId: CORTEX_MEMORY_SHARED_USER_ID,
      limit: limit ?? 100,
    });
    const raw = await sendV2('/v2/ListCortexItems', toJson(ListCortexItemsRequestSchema, req));
    const resp = fromJson(ListCortexItemsResponseSchema, raw);
    return {
      items: (resp.data?.items ?? []).map(toCortexMemoryItem),
      total: resp.data?.total ?? 0,
    };
  },

  getItem: async (id: string, storeId: string): Promise<CortexMemoryItem> => {
    const req = create(GetCortexItemRequestSchema, { id, storeId });
    const raw = await sendV2('/v2/GetCortexItem', toJson(GetCortexItemRequestSchema, req));
    const resp = fromJson(GetCortexItemResponseSchema, raw);
    return toCortexMemoryItem(resp.data!);
  },

  createItem: async (req: CreateCortexMemoryRequest): Promise<Record<string, unknown>> => {
    const messages = (req.messages ?? []).map((m) =>
      create(CortexMessageSchema, { role: m.role, content: m.content })
    );
    const pbReq = create(CreateCortexItemRequestSchema, {
      messages,
      memory: req.memory ?? '',
      userId: CORTEX_MEMORY_SHARED_USER_ID,
      storeId: req.store_id,
      prompt: req.prompt ?? '',
      infer: req.infer !== undefined ? req.infer : undefined,
    });
    const raw = await sendV2('/v2/CreateCortexItem', toJson(CreateCortexItemRequestSchema, pbReq));
    const resp = fromJson(CreateCortexItemResponseSchema, raw);
    return resp.data ? (resp.data as Record<string, unknown>) : {};
  },

  updateItem: async (id: string, storeId: string, data: string): Promise<CortexMemoryItem> => {
    const req = create(UpdateCortexItemRequestSchema, { id, storeId, data });
    const raw = await sendV2('/v2/UpdateCortexItem', toJson(UpdateCortexItemRequestSchema, req));
    const resp = fromJson(UpdateCortexItemResponseSchema, raw);
    return toCortexMemoryItem(resp.data!);
  },

  deleteItem: async (id: string, storeId: string): Promise<void> => {
    const req = create(DeleteCortexItemRequestSchema, { id, storeId });
    await sendV2('/v2/DeleteCortexItem', toJson(DeleteCortexItemRequestSchema, req));
  },

  deleteAllItems: async (storeId: string): Promise<void> => {
    const req = create(DeleteAllCortexItemsRequestSchema, {
      storeId,
      userId: CORTEX_MEMORY_SHARED_USER_ID,
    });
    await sendV2('/v2/DeleteAllCortexItems', toJson(DeleteAllCortexItemsRequestSchema, req));
  },

  search: async (req: SearchCortexMemoryRequest): Promise<CortexMemoryListResponse> => {
    const pbReq = create(SearchCortexItemsRequestSchema, {
      query: req.query,
      storeId: req.store_id,
      userId: CORTEX_MEMORY_SHARED_USER_ID,
      limit: req.limit ?? 0,
    });
    const raw = await sendV2(
      '/v2/SearchCortexItems',
      toJson(SearchCortexItemsRequestSchema, pbReq)
    );
    const resp = fromJson(SearchCortexItemsResponseSchema, raw);
    return {
      items: (resp.data?.items ?? []).map(toCortexMemoryItem),
      total: resp.data?.total ?? 0,
    };
  },
};
